// Diagnóstico del libro de stock (lote 5b de la auditoría del flujo de un día).
// Solo dirección. El informe es EN SECO (no escribe nada). Las dos escrituras
// de saneamiento (consumir las reservas de eventos celebrados y reconstruir
// db.stock desde los lotes) y el inventario de ingredientes a cero se ven antes
// en seco, piden confirmación, hacen copia previa en el servidor y se pueden
// restaurar.
import React, { useCallback, useEffect, useState } from 'react';
import { X, Download, RefreshCw } from 'lucide-react';
import { stockLibroAPI } from '../api/api';

const fmt = (v) => (v === null || v === undefined ? '-' : Number(v).toLocaleString('es-ES', { maximumFractionDigits: 3 }));

const textoError = (error, porDefecto) => error?.response?.data?.detail || porDefecto;

// Operación de saneamiento -> llamada al API
const LLAMADAS = {
  consumir: (body) => stockLibroAPI.consumirReservas(body),
  reconstruir: (body) => stockLibroAPI.reconstruir(body),
  cero: (body) => stockLibroAPI.inventarioACero(body),
};

const ETIQUETAS_OPERACION = {
  consumir_reservas_celebradas: 'Consumo de reservas',
  reconstruir_stock_desde_lotes: 'Reconstrucción de db.stock',
  inventario_a_cero: 'Inventario a cero',
};

const reservasTotales = (r) => (r?.consumir || 0) + (r?.liberar || 0) + (r?.vivas || 0);

const DiagnosticoLibroStock = ({ abierto, onCerrar, onCambio }) => {
  const [diag, setDiag] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [soloRelevantes, setSoloRelevantes] = useState(true);
  const [seco, setSeco] = useState(null); // {tipo, datos}
  const [resultado, setResultado] = useState(null);
  const [respaldos, setRespaldos] = useState([]);
  const [config, setConfig] = useState(null);
  const [trabajando, setTrabajando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [d, r, c] = await Promise.all([
        stockLibroAPI.diagnostico(), stockLibroAPI.respaldos(), stockLibroAPI.configuracion(),
      ]);
      setDiag(d.data);
      setRespaldos(r.data?.respaldos || []);
      setConfig(c.data);
    } catch (e) {
      setError(textoError(e, 'No se ha podido cargar el diagnóstico del libro de stock'));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (abierto) cargar();
  }, [abierto, cargar]);

  if (!abierto) return null;

  const descargarCsv = async () => {
    try {
      const r = await stockLibroAPI.diagnosticoCsv();
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `diagnostico_libro_stock_${diag?.hoy || 'hoy'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(textoError(e, 'No se ha podido descargar el CSV'));
    }
  };

  const verEnSeco = async (tipo) => {
    setTrabajando(true);
    setResultado(null);
    try {
      const r = await LLAMADAS[tipo]({ dry_run: true });
      setSeco({ tipo, datos: r.data });
    } catch (e) {
      window.alert(textoError(e, 'No se ha podido calcular en seco'));
    } finally {
      setTrabajando(false);
    }
  };

  const ejecutar = async (tipo) => {
    let texto;
    if (tipo === 'consumir') {
      texto = 'Se van a consumir las reservas de eventos celebrados (bajan los lotes) y liberar las de presupuestos cancelados. Se hace copia previa y se puede restaurar. ¿Continuar?';
    } else if (tipo === 'reconstruir') {
      texto = 'Se va a reescribir db.stock con la suma de los lotes de cada ingrediente con lotes. Se hace copia previa y se puede restaurar. ¿Continuar?';
    } else {
      const d = seco?.tipo === 'cero' ? seco.datos : {};
      texto = `Se va a poner a 0 TODO el stock de ingredientes: ${d.registros_stock_a_cero ?? 0} registros, ${d.lotes_a_cero ?? 0} lotes y ${reservasTotales(d.reservas)} reservas. Los materiales no se tocan. Se hace copia previa y se puede restaurar. ¿Continuar?`;
    }
    if (!window.confirm(texto)) return;
    setTrabajando(true);
    try {
      const body = { dry_run: false, confirmado: true };
      const r = await LLAMADAS[tipo](body);
      setResultado({ tipo, datos: r.data });
      setSeco(null);
      await cargar();
      if (onCambio) onCambio();
    } catch (e) {
      window.alert(textoError(e, 'No se ha podido ejecutar'));
    } finally {
      setTrabajando(false);
    }
  };

  const restaurar = async (nombre) => {
    if (!window.confirm(`Se va a deshacer la ejecución guardada en ${nombre}. ¿Continuar?`)) return;
    setTrabajando(true);
    try {
      const r = await stockLibroAPI.restaurar(nombre);
      setResultado({ tipo: 'restaurar', datos: r.data });
      await cargar();
      if (onCambio) onCambio();
    } catch (e) {
      window.alert(textoError(e, 'No se ha podido restaurar'));
    } finally {
      setTrabajando(false);
    }
  };

  const cambiarFlag = async (clave, valor) => {
    const texto = clave === 'libro_unico'
      ? (valor ? 'Activar el libro único: confirmar dejará de descontar db.stock de los ingredientes con lotes (solo reserva) y db.stock pasará a calcularse desde los lotes. Actívalo solo después de consumir las reservas celebradas y reconstruir db.stock. ¿Continuar?'
        : 'Apagar el libro único: confirmar vuelve a descontar db.stock como antes. ¿Continuar?')
      : (valor ? 'Activar el consumo automático diario de las reservas de los eventos celebrados ayer (solo funciona con el libro único activo). ¿Continuar?'
        : 'Apagar el consumo automático diario. ¿Continuar?');
    if (!window.confirm(texto)) return;
    try {
      const r = await stockLibroAPI.guardarConfiguracion({ [clave]: valor });
      setConfig(r.data);
    } catch (e) {
      window.alert(textoError(e, 'No se ha podido guardar la configuración'));
    }
  };

  const filas = (diag?.ingredientes || []).filter((f) => !soloRelevantes
    || (f.diferencia !== null && Math.abs(f.diferencia) > 0.0005)
    || (f.reservas_celebradas?.numero || 0) > 0 || (f.salidas_sin_lote?.numero || 0) > 0);
  const t = diag?.totales || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="diagnostico-libro-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl my-8">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Diagnóstico del libro de stock</h2>
            <p className="text-xs text-gray-500">Informe en seco: no cambia nada. {diag?.hoy ? `Calculado a ${diag.hoy}.` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={cargar} disabled={cargando} className="p-2 rounded-lg hover:bg-gray-100" title="Recalcular">
              <RefreshCw className="w-4 h-4 text-gray-600" />
            </button>
            <button type="button" onClick={descargarCsv} disabled={!diag} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 rounded-lg hover:bg-gray-200">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button type="button" onClick={onCerrar} className="p-2 rounded-lg hover:bg-gray-100" title="Cerrar">
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {cargando && <p className="text-sm text-gray-500">Calculando...</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}

          {diag && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-center">
                {[
                  ['Ingredientes', t.ingredientes],
                  ['Con lotes', t.ingredientes_con_lotes],
                  ['Con diferencia', t.ingredientes_con_diferencia],
                  ['Reservas vivas', t.reservas_vivas],
                  ['A consumir', t.reservas_a_consumir],
                  ['A liberar', t.reservas_a_liberar],
                  ['Salidas sin lote', t.salidas_sin_lote],
                ].map(([etiqueta, valor]) => (
                  <div key={etiqueta} className="border rounded-lg p-2">
                    <div className="text-xs text-gray-500">{etiqueta}</div>
                    <div className="text-lg font-bold text-gray-900">{valor ?? 0}</div>
                  </div>
                ))}
              </div>
              {(t.reservas_sin_presupuesto > 0 || t.reservas_coleccion_aparte > 0 || t.reservas_con_aviso > 0) && (
                <p className="text-xs text-amber-700">
                  {t.reservas_sin_presupuesto > 0 && `${t.reservas_sin_presupuesto} reservas de presupuestos que ya no existen (se deciden por su fecha). `}
                  {t.reservas_con_aviso > 0 && `${t.reservas_con_aviso} reservas no caben en su lote (se consumiría lo que hay). `}
                  {t.reservas_coleccion_aparte > 0 && `${t.reservas_coleccion_aparte} documentos en la colección reservas_lotes (no se tocan).`}
                </p>
              )}
              <p className="text-xs text-gray-500">{diag.regla}</p>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={soloRelevantes} onChange={(e) => setSoloRelevantes(e.target.checked)} />
                Solo ingredientes con diferencia, reservas celebradas o salidas sin lote
              </label>

              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-2 py-2 text-left">Ingrediente</th>
                      <th className="px-2 py-2 text-right">db.stock</th>
                      <th className="px-2 py-2 text-right">Lotes</th>
                      <th className="px-2 py-2 text-right">Reservas vivas</th>
                      <th className="px-2 py-2 text-right">A consumir</th>
                      <th className="px-2 py-2 text-right">A liberar</th>
                      <th className="px-2 py-2 text-right">Salidas sin lote</th>
                      <th className="px-2 py-2 text-right">Diferencia</th>
                      <th className="px-2 py-2 text-right">db.stock propuesto</th>
                      <th className="px-2 py-2 text-right">Disponible propuesto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.length === 0 && (
                      <tr><td colSpan={10} className="px-2 py-4 text-center text-gray-400">Nada que revisar</td></tr>
                    )}
                    {filas.map((f) => (
                      <tr key={f.ingrediente_id} className="border-t">
                        <td className="px-2 py-1.5">{f.nombre} <span className="text-gray-400">{f.unidad}</span></td>
                        <td className="px-2 py-1.5 text-right">{fmt(f.stock_registro)}</td>
                        <td className="px-2 py-1.5 text-right">{f.tiene_lotes ? fmt(f.stock_lotes) : 'sin lotes'}</td>
                        <td className="px-2 py-1.5 text-right">{fmt(f.reservas_vivas)}</td>
                        <td className="px-2 py-1.5 text-right">{f.reservas_celebradas.consumir.numero > 0 ? `${fmt(f.consumo_previsto)} (${f.reservas_celebradas.consumir.numero})` : '-'}</td>
                        <td className="px-2 py-1.5 text-right">{f.reservas_celebradas.liberar.numero > 0 ? `${fmt(f.reservas_celebradas.liberar.cantidad)} (${f.reservas_celebradas.liberar.numero})` : '-'}</td>
                        <td className="px-2 py-1.5 text-right">{f.salidas_sin_lote.numero > 0 ? `${fmt(f.salidas_sin_lote.cantidad)} (${f.salidas_sin_lote.numero})` : '-'}</td>
                        <td className={`px-2 py-1.5 text-right ${f.diferencia ? 'text-amber-700 font-semibold' : ''}`}>{fmt(f.diferencia)}</td>
                        <td className="px-2 py-1.5 text-right">{fmt(f.stock_propuesto)}</td>
                        <td className="px-2 py-1.5 text-right">{fmt(f.stock_disponible_propuesto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border rounded-lg p-3 space-y-2 bg-amber-50 border-amber-200">
                <h3 className="text-sm font-semibold text-amber-900">Saneamiento (cambia datos de stock)</h3>
                <p className="text-xs text-amber-800">
                  Orden: primero consumir las reservas celebradas, después reconstruir db.stock y, por último, activar el libro único.
                  Cada ejecución guarda una copia previa que se puede restaurar.
                </p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={trabajando} onClick={() => verEnSeco('consumir')} className="px-3 py-1.5 text-xs bg-white border rounded-lg hover:bg-gray-50">Ver en seco: consumir reservas</button>
                  <button type="button" disabled={trabajando || seco?.tipo !== 'consumir'} onClick={() => ejecutar('consumir')} className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-40">Consumir reservas celebradas</button>
                  <button type="button" disabled={trabajando} onClick={() => verEnSeco('reconstruir')} className="px-3 py-1.5 text-xs bg-white border rounded-lg hover:bg-gray-50">Ver en seco: reconstruir db.stock</button>
                  <button type="button" disabled={trabajando || seco?.tipo !== 'reconstruir'} onClick={() => ejecutar('reconstruir')} className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-40">Reconstruir db.stock</button>
                  <button type="button" disabled={trabajando} onClick={() => verEnSeco('cero')} className="px-3 py-1.5 text-xs bg-white border rounded-lg hover:bg-gray-50">Ver en seco: inventario a cero</button>
                  <button type="button" disabled={trabajando || seco?.tipo !== 'cero'} onClick={() => ejecutar('cero')} className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40">Poner stock de ingredientes a cero</button>
                </div>
                {seco && (
                  <div className="text-xs text-gray-800 bg-white border rounded p-2">
                    {seco.tipo === 'consumir'
                      && `En seco: ${seco.datos.reservas_a_consumir} reservas a consumir y ${seco.datos.reservas_a_liberar} a liberar en ${seco.datos.lotes_afectados} lotes.`}
                    {seco.tipo === 'reconstruir'
                      && `En seco: ${seco.datos.ingredientes_que_cambian} de ${seco.datos.ingredientes_con_lotes} ingredientes con lotes cambiarían en db.stock.`}
                    {seco.tipo === 'cero' && (
                      <>
                        <div>{`En seco: ${seco.datos.registros_stock_a_cero} registros de db.stock y ${seco.datos.lotes_a_cero} lotes quedarían a 0 en ${(seco.datos.ingredientes || []).length} ingredientes. Reservas: ${seco.datos.reservas?.consumir || 0} a consumir (eventos celebrados), ${seco.datos.reservas?.liberar || 0} a liberar (cancelados o rechazados) y ${seco.datos.reservas?.vivas || 0} vivas que se liberan.`}</div>
                        <div className="text-gray-500">{seco.datos.regla}</div>
                      </>
                    )}
                    {(seco.datos.avisos || []).map((a) => <div key={a} className="text-amber-700">{a}</div>)}
                  </div>
                )}
                {resultado && (
                  <div className="text-xs text-green-800 bg-white border border-green-200 rounded p-2">
                    {resultado.tipo === 'consumir' && `Hecho: ${resultado.datos.consumidas} reservas consumidas y ${resultado.datos.liberadas} liberadas. Copia previa: ${resultado.datos.respaldo || 'ninguna (no había nada)'}.`}
                    {resultado.tipo === 'reconstruir' && `Hecho: ${resultado.datos.registros_escritos} registros de db.stock escritos y ${resultado.datos.registros_creados} creados. Copia previa: ${resultado.datos.respaldo}.`}
                    {resultado.tipo === 'cero' && `Hecho: ${resultado.datos.registros_escritos} registros de db.stock y ${resultado.datos.lotes_escritos} lotes a 0, ${resultado.datos.movimientos_creados} movimientos de ajuste, ${resultado.datos.reservas?.consumidas || 0} reservas consumidas y ${resultado.datos.reservas?.liberadas || 0} liberadas. Copia previa: ${resultado.datos.respaldo || 'ninguna (no había nada)'}.`}
                    {resultado.tipo === 'cero' && (resultado.datos.avisos || []).map((a) => <div key={a} className="text-amber-700">{a}</div>)}
                    {resultado.tipo === 'restaurar' && `Restaurada la copia ${resultado.datos.respaldo}.`}
                  </div>
                )}
                {respaldos.length > 0 && (
                  <div className="text-xs">
                    <div className="font-semibold text-gray-700 mb-1">Copias previas</div>
                    {respaldos.map((r) => (
                      <div key={r.nombre} className="flex items-center justify-between border-t py-1">
                        <span>{r.nombre} · {ETIQUETAS_OPERACION[r.operacion] || r.operacion} · {r.creado_por || ''}</span>
                        {r.restaurado_en
                          ? <span className="text-gray-400">Restaurada</span>
                          : <button type="button" disabled={trabajando} onClick={() => restaurar(r.nombre)} className="px-2 py-0.5 border rounded hover:bg-gray-50">Restaurar</button>}
                      </div>
                    ))}
                  </div>
                )}
                {config && (
                  <div className="flex flex-wrap gap-4 text-xs text-gray-800 pt-1">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!config.libro_unico} onChange={(e) => cambiarFlag('libro_unico', e.target.checked)} />
                      Libro único activo (stock.libro_unico)
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={!!config.consumo_automatico} onChange={(e) => cambiarFlag('consumo_automatico', e.target.checked)} />
                      Consumo automático diario (stock.consumo_automatico)
                    </label>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiagnosticoLibroStock;
